import React, { useState, useEffect, useContext } from "react";
import "react-dates/initialize";
// import { makeStyles } from '@material-ui/core/styles';
// import MenuItem from '@material-ui/core/MenuItem';
// import FormControl from '@material-ui/core/FormControl';
// import Select from '@material-ui/core/Select';
import moment from "moment";
import { DateRangePicker } from "react-dates";
import { GeoJsonLayer, IconLayer } from "@deck.gl/layers";

import config from "../../dashboard-config";

/* local components */
import GraphDataCardPane from "../GraphDataCards/GraphDataCardPane";
import Timeseries from "./Timeseries";

/* contexts for hooks */
import { ChartDataContext } from "../../context/ChartDataContext";
import { SessionContext } from "../../context/SessionContext";

/* custom styles */
import "./Graphs.scss";
import "react-dates/lib/css/_datepicker.css";
import "./react_dates_overrides.scss";

/* https://www.apollographql.com/docs/react/api/react-hooks/ */
import { useApolloClient } from "@apollo/react-hooks";
import { GET_BEST_TIME_SLICE_VALUES } from "../GraphQL/GetTimeSliceValues";

// max number of points to query from wavefleet in single page
const WF_LOC_PAGE_LIMIT = 3000;

const graphStore = {
  wave: {
    name: "Significant Wave Height",
    tooltipName: "Significant Wave Height",
    graph: ["significantWaveHeight"],
    modelData: [
      {
        variableId: 20
      }
    ]
  },
  period: {
    name: "Peak Period, Mean Period",
    tooltipName: ["Peak Period", "Mean Period"],
    graph: ["peakPeriod", "meanPeriod"],
    modelData: [
      {
        variableId: 0
      },
      {
        variableId: 12
      }
    ]
  },
  direction: {
    name: "Peak Direction, Mean Direction",
    tooltipName: ["Peak Direction", "Mean Direction"],
    graph: ["peakDirection", "meanDirection"],
    modelData: [
      {
        variableId: 32
      },
      {
        variableId: 7
      }
    ]
  },
  spread: {
    name: "Peak Spread, Mean Spread",
    tooltipName: ["Peak Spread", "Mean Spread"],
    graph: ["peakDirectionalSpread", "meanDirectionalSpread"],
    modelData: [
      {
        variableId: 0
      },
      {
        variableId: 26
      }
    ]
  }
};

const Graphs = ({ deck, pointData, sizeScale, spotterId }) => {
  const client = useApolloClient();

  const [focusedInput, setFocusedInput] = useState(null);
  const [fullGraphData, setFullGraphData] = useState(null);
  const [graphData, setGraphData] = useState(null);
  const [modelOTsDataLoading, setModelOTsDataLoading] = useState(true);
  const [optimalPointwiseData, setOptimalPointwiseData] = useState(null);
  const [spotterTsDataLoading, setSpotterTsDataLoading] = useState(true);
  const [startDate, setStartDate] = useState(
    moment()
      .subtract(7, "days")
      .startOf("day")
  );
  const [endDate, setEndDate] = useState(
    moment()
      .add(7, "days")
      .endOf("day")
  );
  const { queriedData, selectedData, trailTimestamp } = useContext(
    ChartDataContext
  );
  const { currentTrial } = useContext(SessionContext);
  const profile = JSON.parse(localStorage.getItem("sofar-user") || "{}");

  const graphRender = () => {
    const current = graphStore[selectedData.selectedGraph]["graph"];
    const optimalPointWiseData = optimalPointwiseData.map(record =>
      record.value <= -999 ? null : record.value.toFixed(2)
    );
    return graphData ? (
      <Timeseries
        startDate={startDate}
        endDate={endDate}
        observationData={graphData}
        optimalPointwiseData={optimalPointWiseData}
        optimalPointwiseTimestampData={optimalPointwiseData.map(
          record => record.instant.split(["["])[0]
        )}
        selectedChart={current}
        selectedGraph={selectedData.selectedGraph}
        timestampData={graphData.map(record => record.timestamp)}
        tooltipName={graphStore[selectedData.selectedGraph]["tooltipName"]}
      />
    ) : (
      <Timeseries
        startDate={startDate}
        endDate={endDate}
        optimalPointwiseData={optimalPointWiseData}
        optimalPointwiseTimestampData={optimalPointwiseData.map(
          record => record.instant.split(["["])[0]
        )}
        selectedChart={current}
        selectedGraph={selectedData.selectedGraph}
        tooltipName={graphStore[selectedData.selectedGraph]["tooltipName"]}
      />
    );
  };

  useEffect(() => {
    let fetchLoad = true;
    const apiUrlSingleSpotter = `${config.wavefleetUrl}/oceans-api/wave-data?spotterId`;

    let start = moment(startDate)
      .startOf("day")
      .utc()
      .toISOString();
    let end = endDate
      ? moment(endDate)
          .utc()
          .toISOString()
      : moment()
          .utc()
          .toISOString();
    let longitude, latitude;

    if (spotterId) {
      let {
        latitude: spotterLatitude,
        longitude: spotterLongitude
      } = selectedData.selectedSpotter.track[0];
      latitude = spotterLatitude;
      longitude =
        spotterLongitude < 0 ? spotterLongitude + 360 : spotterLongitude;

      const fetchSpotterWaveData = async () => {
        setSpotterTsDataLoading(true);
        const res = await fetch(
          `${config.wavefleetUrl}/oceans-api/wave-data?spotterId=${spotterId}&startDate=${start}&endDate=${end}&limit=${WF_LOC_PAGE_LIMIT}`,
          {
            method: "GET",
            headers: new Headers({
              token: profile.token
            })
          }
        );

        let data = await res.json();
        return data;
      };

      /* paging fetch - single spotter data for graph */
      fetchSpotterWaveData().then(data => {
        if (fetchLoad) {
          let tempArr = data.data.waves.map(record => record);
          setGraphData([...tempArr]);
          setSpotterTsDataLoading(false);
        }
      });

      let totalFetchedData = [];

      /* paging function for map track spotter data*/
      const fetchPageSpotterTrackData = async function(
        startDate = "1970-01-01T00:00Z"
      ) {
        return await fetch(
          apiUrlSingleSpotter +
            `=${spotterId}&startDate=${startDate}&limit=${WF_LOC_PAGE_LIMIT}&includeWaves=false&includeTrack=true`,
          {
            method: "GET",
            headers: new Headers({
              token: profile.token
            })
          }
        ).then(res => {
          return res.json();
        });
      };

      /* trail data */
      const getEntireSpotterData = async function(
        startDate = "1970-01-01T00:00Z"
      ) {
        let responseLength = WF_LOC_PAGE_LIMIT;
        let lastDate = startDate;

        while (responseLength === WF_LOC_PAGE_LIMIT) {
          let pageResults = await fetchPageSpotterTrackData(lastDate);
          let trackArr = pageResults.data.track;
          responseLength = trackArr.length;
          if (responseLength) {
            lastDate = trackArr[trackArr.length - 1].timestamp;
            totalFetchedData = [...totalFetchedData, ...pageResults.data.track];
            // commenting in the line below will make tracks animate as they're built
            // setFullGraphData(totalFetchedData);
          }
        }
        return totalFetchedData;
      };

      getEntireSpotterData().then(data => {
        if (fetchLoad) {
          setFullGraphData(totalFetchedData);
        }
      });
    }

    setGraphData(null);

    setModelOTsDataLoading(true);
    const currentVariableId =
      selectedData.selectedGraph === "wave"
        ? graphStore[selectedData.selectedGraph].modelData[0].variableId
        : graphStore[selectedData.selectedGraph].modelData[1].variableId;

    if (pointData) {
      latitude = pointData[1];
      longitude = pointData[0] + 360;
    }

    client
      .query({
        fetchPolicy: "cache-first",
        query: GET_BEST_TIME_SLICE_VALUES,
        context: { clientName: "wexGeo" },
        variables: {
          input: {
            dataSourceId: 19,
            variableId: currentVariableId,
            start: startDate,
            end: endDate,
            location: { latitude, longitude }
          }
        },
        onError: e => console.log(e)
      })
      .then(({ data }) => {
        setOptimalPointwiseData(data.bestTimeSliceValues);
        setModelOTsDataLoading(false);
      });

    return () => (fetchLoad = false);
  }, [
    pointData,
    spotterId,
    startDate,
    endDate,
    selectedData.selectedGraph,
    client
  ]);

  useEffect(() => {
    if (!graphData || !fullGraphData || !selectedData.selectedSpotter) {
      // || !isToggled ) {
      const otherLayers = deck.props.layers.filter(
        l =>
          l.id !== "my-spotter-trail-layer" && l.id !== "my-spotter-ghost-layer"
      );
      const noData = {
        type: "FeatureCollection",
        features: []
      };

      deck.setProps({
        layers: [
          ...otherLayers,
          new GeoJsonLayer({ id: "my-spotter-trail-layer", data: noData }),
          new IconLayer({ id: "my-spotter-ghost-layer", data: [] })
        ]
      });

      return;
    }

    const trailData = fullGraphData.map(record => [
      record.longitude,
      record.latitude
    ]);
    const trackingData = graphData.map(record => [
      new Date(record.timestamp).getTime(),
      [record.longitude, record.latitude],
      record.peakDirection
    ]);
    const layers = [];
    const trailTimestampEpoch = new Date(trailTimestamp).getTime();

    for (let i = 0; i < trackingData.length; i++) {
      if (trackingData[i][0] < trailTimestampEpoch) continue;

      layers.push(
        new IconLayer({
          id: "my-spotter-ghost-layer",
          data: trackingData[i],
          pickable: true,
          stroked: true,
          filled: false,
          extruded: false,
          getIcon: () => {
            return {
              url: "spotter-icon-default.svg",
              x: 0,
              y: 0,
              width: 40,
              height: 40
            };
          },
          sizeScale: sizeScale,
          getAngle: () => trackingData[i][2],
          getPosition: () => trackingData[i][1],
          getSize: 30,
          getFillColor: () => [233, 190, 64]
        })
      );

      break;
    }

    const filtered = trailData.filter(d => d[0] < -125 || d[0] > -120);
    const set1 = filtered.map(d =>
      d[0] < 0 ? [d[0], d[1]] : [d[0] - 360, d[1]]
    );
    const set2 = filtered.map(d =>
      d[0] < 0 ? [d[0] + 360, d[1]] : [d[0], d[1]]
    );

    const data = {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: set1
          }
        },
        {
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: set2
          }
        }
      ]
    };

    layers.push(
      new GeoJsonLayer({
        id: "my-spotter-trail-layer",
        data,
        opacity: 1,
        pickable: false,
        stroked: true,
        filled: false,
        extruded: false,
        lineWidthScale: 1,
        lineWidthMinPixels: 1,
        getLineColor: [233, 190, 64],
        getLineWidth: 3
      })
    );

    // deck.setProps({ layers });
    const otherLayers = deck.props.layers.filter(
      l =>
        l.id !== "my-spotter-trail-layer" && l.id !== "my-spotter-ghost-layer"
    );

    deck.setProps({ layers: [...otherLayers, ...layers] });
  }, [
    deck,
    fullGraphData,
    graphData,
    trailTimestamp,
    selectedData.selectedSpotter,
    sizeScale
  ]); // isToggled, trailTimestamp, selectedSpotter, sizeScale ]);

  const renderGraphs = () => {
    if (!spotterTsDataLoading && !modelOTsDataLoading) {
      if (optimalPointwiseData) {
        return <div style={{ paddingTop: "0px" }}>{graphRender()}</div>;
      }
    } else if (!modelOTsDataLoading) {
      if (pointData && optimalPointwiseData) {
        return <div style={{ paddingTop: "0px" }}>{graphRender()}</div>;
      }
    }
  };

  return (
    <React.Fragment>
      {selectedData && (
        <React.Fragment>
          {queriedData && optimalPointwiseData && (
            <GraphDataCardPane
              selectedGraph={selectedData.selectedGraph}
              queriedData={queriedData}
              modelPoint={
                optimalPointwiseData[Math.ceil(optimalPointwiseData.length / 2)]
              }
            />
          )}
          {currentTrial.isAdmin && (
            <div
              style={{
                right: "0",
                marginRight: "65px",
                marginTop: "20px",
                position: "fixed",
                zIndex: "1000"
              }}
            >
              <DateRangePicker
                startDate={startDate} // momentPropTypes.momentObj or null,
                startDateId="start_date" // PropTypes.string.isRequired,
                endDate={endDate} // momentPropTypes.momentObj or null,
                endDateId="end_date" // PropTypes.string.isRequired,
                // inputIconPosition='after'
                showDefaultInputIcon
                onDatesChange={({ startDate, endDate }) => {
                  setStartDate(startDate);
                  if (endDate && endDate.isBefore(startDate)) {
                    setEndDate(startDate);
                  }
                  setEndDate(endDate);
                }} // PropTypes.func.isRequired,
                focusedInput={focusedInput} // PropTypes.oneOf([START_DATE, END_DATE]) or null,
                onFocusChange={focusedInput => setFocusedInput(focusedInput)} // PropTypes.func.isRequired,
                openDirection="up"
                isOutsideRange={() => false}
                hideKeyboardShortcutsPanel={true}
              />
            </div>
          )}
          {renderGraphs()}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default Graphs;
